import React from "react";
import client from "@culturehq/client";
import { Button, Table, Warning } from "@culturehq/components";

import { useSortable } from "app/hooks";
import { errorService, Trans } from "app/utils";

import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import SurveyEditModal from "./SurveyEditModal";
import SurveyItemEditModal from "./SurveyItemEditModal";
import SurveyItemListRow from "./SurveyItemListRow";
import SurveyPreview from "./SurveyPreview";
import SurveyCompleted from "./SurveyCompleted";

const SurveyItemList = ({ event, survey, onSurveyDeleted, onSurveyEdited }) => {
  const onSurveyItemAdded = surveyItem => {
    onSurveyEdited({
      ...survey,
      surveyItems: [...survey.surveyItems, surveyItem]
    });
  };

  const onSurveyDelete = async () => {
    try {
      await client.deleteSurvey({ surveyId: survey.id });
      onSurveyDeleted();
    } catch (error) {
      errorService.error(error);
    }
  };

  const onSurveyItemDeleted = async surveyItem => {
    try {
      await client.deleteSurveyItem({ surveyItemId: surveyItem.id });

      onSurveyEdited({
        ...survey,
        surveyItems: survey.surveyItems.filter(current => (
          current.id !== surveyItem.id
        ))
      });
    } catch (error) {
      errorService.error(error);
    }
  };

  const onSurveyItemUpdated = surveyItem => {
    onSurveyEdited({
      ...survey,
      surveyItems: survey.surveyItems.map(current => (
        current.id === surveyItem.id ? surveyItem : current
      ))
    });
  };

  const sortableRef = useSortable({
    handle: ".sortable__drag-handler",
    onSort: sortEvent => client.reorderSurveyItems({
      surveyId: survey.id,
      order: Array.from(sortEvent.to.children).map(surveyItem => (
        surveyItem.getAttribute("data-id")
      ))
    })
  });

  return (
    <div className="Manage SurveyItems">
      <div className="row SurveyItems__actions">
        <div className="col-sm-12">
          <SurveyItemEditModal
            survey={survey}
            onCallback={onSurveyItemAdded}
            surveyItem={null}
            isEdit={false}
          >
            {onTrigger => (
              <Button icon="plus" onClick={onTrigger}>
                <Trans>Add Survey Question</Trans>
              </Button>
            )}
          </SurveyItemEditModal>
          {survey.surveyItems.length > 0 && (
            <SurveyPreview survey={survey}>
              {onTrigger => (
                <Button icon="clipboard" inverted onClick={onTrigger}>
                  <Trans>View Preview</Trans>
                </Button>
              )}
            </SurveyPreview>
          )}
          <SurveyEditModal survey={survey} event={event} onEditSuccess={onSurveyEdited}>
            {onTrigger => (
              <Button icon="edit" inverted onClick={onTrigger}>
                <Trans>Edit Survey</Trans>
              </Button>
            )}
          </SurveyEditModal>
          {!event && (
            <SurveyCompleted survey={survey}>
              {onTrigger => (
                <Button icon="stats-bars" inverted onClick={onTrigger}>
                  <Trans>Results</Trans>
                </Button>
              )}
            </SurveyCompleted>
          )}
          <ChqModalConfirmDelete
            onAccept={onSurveyDelete}
            trigger={onTrigger => (
              <Button onClick={onTrigger} icon="ios-trash" inverted>
                <Trans>Delete Survey</Trans>
              </Button>
            )}
          >
            <Trans>
              Are you sure you would like to delete this survey? This will erase
              all data previously collected.
            </Trans>
          </ChqModalConfirmDelete>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <Table>
            <thead>
              <tr>
                <th />
                <th />
                <th>Question</th>
                <th>Item Type</th>
                <th className="pull-right">Actions</th>
              </tr>
            </thead>
            <tbody ref={sortableRef}>
              {survey.surveyItems.map(surveyItem => (
                <SurveyItemListRow
                  key={surveyItem.id}
                  surveyItem={surveyItem}
                  survey={survey}
                  event={event}
                  onSurveyItemUpdated={onSurveyItemUpdated}
                  onSurveyItemDeleted={onSurveyItemDeleted}
                />
              ))}
            </tbody>
          </Table>

          {survey.surveyItems.length < 1 && (
            <Warning>
              No survey questions configured. Please add your questions.
            </Warning>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyItemList;
